// file: src/pages/AdminLogsPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Badge,
  Button,
  IconButton,
  Input,
  Select,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  HStack,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Tooltip,
  Tag,
  TagLabel,
  TagRightIcon,
  Code,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Wrap,
  WrapItem,
  useDisclosure,
} from "@chakra-ui/react";
import {
  SearchIcon,
  RepeatIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InfoOutlineIcon,
  ExternalLinkIcon,
  TimeIcon,
} from "@chakra-ui/icons";

import { useCognito } from "../utils/useCognito";

const API_BASE =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getAdminLogs";

const PAGE_SIZE = 50;

const quickRanges = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
];

function toIsoIfSet(value) {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

function formatTimestamp(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString();
}

function getQuickRange(key) {
  const now = new Date();
  const end = new Date(now);
  let start;
  if (key === "today") {
    start = new Date(now);
    start.setHours(0, 0, 0, 0);
  } else if (key === "7d") {
    start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  } else if (key === "30d") {
    start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  } else {
    return { from: "", to: "" };
  }

  const toLocalInput = (d) => {
    // datetime-local expects "YYYY-MM-DDTHH:mm"
    const pad = (n) => String(n).padStart(2, "0");
    const y = d.getFullYear();
    const m = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${y}-${m}-${day}T${hh}:${mm}`;
  };

  return {
    from: toLocalInput(start),
    to: toLocalInput(end),
  };
}

const AdminLogsPage = () => {
  const toast = useToast();
  const { getIdToken } = useCognito();
  const [logs, setLogs] = useState([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");

  const [filters, setFilters] = useState({
    adminUserId: "",
    action: "",
    targetId: "",
    from: "",
    to: "",
  });

  const [selectedLog, setSelectedLog] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const hasMore = Boolean(nextToken);
  const isBusy = isLoadingInitial || isLoadingMore;

  const handleChangeFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleQuickRange = (key) => {
    const { from, to } = getQuickRange(key);
    setFilters((prev) => ({
      ...prev,
      from,
      to,
    }));
  };

  const resetFilters = () => {
    setFilters({
      adminUserId: "",
      action: "",
      targetId: "",
      from: "",
      to: "",
    });
  };

  const fetchLogs = useCallback(
    async ({ append = false } = {}) => {
      const token = await getIdToken();
      if (!token) {
        toast({
          title: "Not authenticated",
          description: "Please log in again to view admin logs.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      try {
        if (append) {
          setIsLoadingMore(true);
        } else {
          setIsLoadingInitial(true);
        }

        const params = new URLSearchParams();

        if (filters.adminUserId.trim()) {
          params.append("adminUserId", filters.adminUserId.trim());
        }
        if (filters.action.trim()) {
          params.append("action", filters.action.trim());
        }
        if (filters.targetId.trim()) {
          params.append("targetId", filters.targetId.trim());
        }

        const fromIso = toIsoIfSet(filters.from);
        const toIso = toIsoIfSet(filters.to);

        if (fromIso) params.append("from", fromIso);
        if (toIso) params.append("to", toIso);

        params.append("limit", String(PAGE_SIZE));
        params.append("sortDirection", sortDirection);

        if (append && nextToken) {
          params.append("nextToken", nextToken);
        }

        const url = `${API_BASE}?${params.toString()}`;

        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(
            `API error ${res.status}: ${res.statusText || text || "Unknown"}`
          );
        }

        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];

        setLogs((prev) => (append ? [...prev, ...items] : items));
        setNextToken(data.nextToken || null);

        if (!append && items.length === 0) {
          toast({
            title: "No logs found",
            description: "Try adjusting your filters or date range.",
            status: "info",
            duration: 4000,
            isClosable: true,
          });
        }
      } catch (err) {
        console.error("Failed to fetch admin logs:", err);
        toast({
          title: "Error loading logs",
          description: err.message || "An unexpected error occurred.",
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      } finally {
        setIsLoadingInitial(false);
        setIsLoadingMore(false);
      }
    },
    [filters, sortDirection, nextToken, getIdToken, toast]
  );

  // Initial load
  useEffect(() => {
    // Slight delay so layout can render first
    fetchLogs({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilters = () => {
    setNextToken(null);
    fetchLogs({ append: false });
  };

  const handleLoadMore = () => {
    if (!hasMore || isBusy) return;
    fetchLogs({ append: true });
  };

  const handleToggleSort = () => {
    setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
    // After switching sort, re-fetch fresh page
    setNextToken(null);
    fetchLogs({ append: false });
  };

  const handleRowClick = (log) => {
    setSelectedLog(log);
    onOpen();
  };

  const derivedStats = useMemo(() => {
    if (!logs.length) return null;
    const total = logs.length;
    const byAdmin = new Set();
    const byAction = new Set();
    let newest = null;
    logs.forEach((log) => {
      if (log.adminUserId) byAdmin.add(log.adminUserId);
      if (log.action) byAction.add(log.action);
      if (log.timestamp) {
        const t = new Date(log.timestamp).getTime();
        if (!newest || t > newest) newest = t;
      }
    });
    return {
      total,
      uniqueAdmins: byAdmin.size,
      uniqueActions: byAction.size,
      newest: newest ? new Date(newest) : null,
    };
  }, [logs]);

  return (
    <Box
      p={{ base: 4, md: 8 }}
      maxW="1300px"
      mx="auto"
      display="flex"
      flexDirection="column"
      gap={6}
    >
      {/* Header / Hero */}
      <Box
        borderRadius="2xl"
        bgGradient="linear(to-r, teal.500, blue.500)"
        color="white"
        p={{ base: 4, md: 6 }}
        boxShadow="lg"
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          gap={4}
        >
          <Box>
            <Heading size="lg" mb={2}>
              Admin Activity Logs
            </Heading>
            <Text opacity={0.9}>
              High-signal audit trail for every admin action. Filter, inspect,
              and export with zero drama.
            </Text>
          </Box>
          <HStack spacing={3}>
            <Tooltip label="Refresh logs from the server">
              <IconButton
                aria-label="Refresh logs"
                icon={<RepeatIcon />}
                size="md"
                variant="outline"
                borderColor="whiteAlpha.800"
                colorScheme="whiteAlpha"
                _hover={{
                  transform: "scale(1)",
                  bg: "whiteAlpha.200",
                }}
                onClick={() => {
                  setNextToken(null);
                  fetchLogs({ append: false });
                }}
              />
            </Tooltip>
            <Tooltip label="Open latest log in detail view">
              <Button
                size="md"
                variant="solid"
                bg="white"
                color="teal.600"
                _hover={{
                  transform: "scale(1)",
                  bg: "whiteAlpha.900",
                  boxShadow: "md",
                }}
                rightIcon={<ExternalLinkIcon />}
                onClick={() => {
                  if (logs.length) {
                    handleRowClick(logs[0]);
                  }
                }}
              >
                Quick inspect
              </Button>
            </Tooltip>
          </HStack>
        </Flex>
      </Box>

      {/* Filters + Sort */}
      <Box
        borderRadius="2xl"
        bg="white"
        color="gray.800"
        p={5}
        boxShadow="xl"
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Flex
          direction={{ base: "column", lg: "row" }}
          gap={4}
          align="flex-start"
          justify="space-between"
        >
          <VStack align="stretch" spacing={3} flex="1.5">
            <HStack spacing={3}>
              <Box flex="1">
                <Text fontSize="sm" mb={1} color="gray.700">
                  Admin User ID
                </Text>
                <Input
                  placeholder="auth0|... / cognito sub / email fragment"
                  size="sm"
                  value={filters.adminUserId}
                  onChange={(e) =>
                    handleChangeFilter("adminUserId", e.target.value)
                  }
                  bg="white"
                  borderColor="gray.300"
                />
              </Box>
              <Box flex="1">
                <Text fontSize="sm" mb={1} color="gray.700">
                  Action
                </Text>
                <Input
                  placeholder="e.g., admin_viewAllOrders"
                  size="sm"
                  value={filters.action}
                  onChange={(e) =>
                    handleChangeFilter("action", e.target.value)
                  }
                  bg="white"
                  borderColor="gray.300"
                />
              </Box>
            </HStack>

            <HStack spacing={3}>
              <Box flex="1">
                <Text fontSize="sm" mb={1} color="gray.700">
                  Target ID
                </Text>
                <Input
                  placeholder="IVR / Order / Invoice / 'ALL'"
                  size="sm"
                  value={filters.targetId}
                  onChange={(e) =>
                    handleChangeFilter("targetId", e.target.value)
                  }
                  bg="white"
                  borderColor="gray.300"
                />
              </Box>
              <Box flex="1">
                <Text fontSize="sm" mb={1} color="gray.700">
                  Sort by time
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  width="100%"
                  rightIcon={
                    sortDirection === "desc" ? (
                      <ChevronDownIcon />
                    ) : (
                      <ChevronUpIcon />
                    )
                  }
                  _hover={{
                    transform: "scale(1)",
                    bg: "gray.50",
                  }}
                  onClick={handleToggleSort}
                >
                  {sortDirection === "desc"
                    ? "Newest → Oldest"
                    : "Oldest → Newest"}
                </Button>
              </Box>
            </HStack>

            <HStack spacing={3} align="center">
              <Box flex="1">
                <Text fontSize="sm" mb={1} color="gray.700">
                  From
                </Text>
                <Input
                  type="datetime-local"
                  size="sm"
                  value={filters.from}
                  onChange={(e) => handleChangeFilter("from", e.target.value)}
                  bg="white"
                  borderColor="gray.300"
                />
              </Box>
              <Box flex="1">
                <Text fontSize="sm" mb={1} color="gray.700">
                  To
                </Text>
                <Input
                  type="datetime-local"
                  size="sm"
                  value={filters.to}
                  onChange={(e) => handleChangeFilter("to", e.target.value)}
                  bg="white"
                  borderColor="gray.300"
                />
              </Box>
            </HStack>

            <HStack spacing={3}>
              <Wrap spacing={2}>
                {quickRanges.map((r) => (
                  <WrapItem key={r.key}>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="teal"
                      _hover={{
                        transform: "scale(1)",
                        bg: "teal.50",
                      }}
                      onClick={() => handleQuickRange(r.key)}
                      leftIcon={<TimeIcon boxSize={3} />}
                    >
                      {r.label}
                    </Button>
                  </WrapItem>
                ))}
              </Wrap>
            </HStack>
          </VStack>

          <Divider
            orientation="vertical"
            display={{ base: "none", lg: "block" }}
          />

          {/* Right side: actions + mini stats */}
          <VStack
            align="stretch"
            spacing={3}
            flex="1"
            minW={{ base: "100%", lg: "260px" }}
          >
            <Text fontSize="sm" color="gray.700">
              Controls
            </Text>
            <HStack spacing={3}>
              <Button
                size="sm"
                colorScheme="teal"
                leftIcon={<SearchIcon />}
                _hover={{
                  transform: "scale(1)",
                  boxShadow: "md",
                }}
                onClick={handleApplyFilters}
                isLoading={isLoadingInitial}
                loadingText="Loading"
              >
                Apply filters
              </Button>
              <Button
                size="sm"
                variant="ghost"
                _hover={{
                  transform: "scale(1)",
                  bg: "gray.50",
                }}
                onClick={resetFilters}
              >
                Reset
              </Button>
            </HStack>

            <Box
              mt={2}
              p={3}
              borderRadius="lg"
              bg="gray.50"
              borderWidth="1px"
              borderColor="gray.200"
            >
              <HStack justify="space-between" align="flex-start">
                <Box>
                  <Text fontSize="xs" color="gray.600" mb={1}>
                    Snapshot (current page)
                  </Text>
                  {derivedStats ? (
                    <HStack spacing={4}>
                      <Stat>
                        <StatLabel fontSize="xs" color="gray.600">
                          Logs
                        </StatLabel>
                        <StatNumber fontSize="md">
                          {derivedStats.total}
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel fontSize="xs" color="gray.600">
                          Admins
                        </StatLabel>
                        <StatNumber fontSize="md">
                          {derivedStats.uniqueAdmins}
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel fontSize="xs" color="gray.600">
                          Actions
                        </StatLabel>
                        <StatNumber fontSize="md">
                          {derivedStats.uniqueActions}
                        </StatNumber>
                      </Stat>
                    </HStack>
                  ) : (
                    <Text fontSize="xs" color="gray.600">
                      Run a search to see stats.
                    </Text>
                  )}
                </Box>
                <Tooltip
                  label="All timestamps are in your local timezone, but stored as ISO strings in DynamoDB."
                  maxW="260px"
                >
                  <Box cursor="help">
                    <InfoOutlineIcon color="gray.600" />
                  </Box>
                </Tooltip>
              </HStack>
              {derivedStats?.newest && (
                <Stat mt={1}>
                  <StatHelpText fontSize="xs" color="gray.600">
                    Latest event:{" "}
                    {derivedStats.newest.toLocaleString(undefined, {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </StatHelpText>
                </Stat>
              )}
            </Box>
          </VStack>
        </Flex>
      </Box>

      {/* Logs table */}
      <Box
        borderRadius="2xl"
        bg="white"
        color="gray.800"
        p={5}
        boxShadow="xl"
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Flex justify="space-between" align="center" mb={3}>
          <HStack spacing={3}>
            <Heading size="sm">Results</Heading>
            <Badge
              colorScheme={logs.length ? "teal" : "gray"}
              borderRadius="full"
              px={2}
            >
              {logs.length} log{logs.length === 1 ? "" : "s"}
            </Badge>
          </HStack>
          {isBusy && (
            <HStack spacing={2} fontSize="sm" color="gray.400">
              <Spinner size="sm" />
              <Text>Syncing with DynamoDB…</Text>
            </HStack>
          )}
        </Flex>

        <Box
          borderRadius="xl"
          borderWidth="1px"
          borderColor="gray.200"
          overflow="hidden"
        >
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead bg="gray.100">
                <Tr>
                  <Th>Time</Th>
                  <Th>Admin</Th>
                  <Th>Action</Th>
                  <Th>Target</Th>
                  <Th>Notes</Th>
                  <Th>Log ID</Th>
                </Tr>
              </Thead>
              <Tbody>
                {logs.length === 0 && !isBusy && (
                  <Tr>
                    <Td colSpan={6}>
                      <Flex
                        py={6}
                        align="center"
                        justify="center"
                        direction="column"
                        gap={2}
                      >
                        <Text fontSize="sm" color="gray.500">
                          No logs yet for this filter set.
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          Try widening the date range or removing some filters.
                        </Text>
                      </Flex>
                    </Td>
                  </Tr>
                )}

                {logs.map((log) => (
                  <Tr
                    key={log.logId}
                    _hover={{
                      bg: "gray.50",
                      cursor: "pointer",
                    }}
                    onClick={() => handleRowClick(log)}
                  >
                    <Td whiteSpace="nowrap" maxW="180px">
                      <Text fontSize="sm">{formatTimestamp(log.timestamp)}</Text>
                    </Td>
                    <Td maxW="220px">
                      <Tooltip
                        label={log.adminUserId}
                        isDisabled={!log.adminUserId}
                      >
                        <Text fontSize="sm" noOfLines={1} title={log.adminUserId || undefined}>
                          {log.adminUserId || "—"}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td maxW="220px">
                      <Tag
                        size="sm"
                        variant="subtle"
                        colorScheme={
                          log.action?.includes("delete")
                            ? "red"
                            : log.action?.includes("update")
                            ? "yellow"
                            : "teal"
                        }
                      >
                        <TagLabel maxW="180px" noOfLines={1} title={log.action || undefined}>
                          {log.action || "—"}
                        </TagLabel>
                      </Tag>
                    </Td>
                    <Td maxW="160px">
                      <Badge
                        variant="outline"
                        colorScheme={
                          log.targetId === "ALL"
                            ? "purple"
                            : log.targetId
                            ? "blue"
                            : "gray"
                        }
                        borderRadius="full"
                        px={2}
                      >
                        <Text fontSize="xs" noOfLines={1} title={log.targetId || undefined}>
                          {log.targetId || "—"}
                        </Text>
                      </Badge>
                    </Td>
                    <Td maxW="260px">
                      <Text fontSize="sm" noOfLines={1} color="gray.700" title={log.notes || undefined}>
                        {log.notes || "—"}
                      </Text>
                    </Td>
                    <Td maxW="220px">
                      <Box maxW="220px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                        <Code
                          fontSize="xs"
                          bg="gray.100"
                          borderRadius="md"
                          px={2}
                          py={1}
                        >
                          {log.logId || "—"}
                        </Code>
                      </Box>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>

        {/* Load more */}
        {hasMore && (
          <Flex justify="center" mt={4}>
            <Button
              size="sm"
              variant="outline"
              colorScheme="teal"
              _hover={{
                transform: "scale(1)",
                boxShadow: "md",
              }}
              onClick={handleLoadMore}
              isLoading={isLoadingMore}
              loadingText="Loading more"
            >
              Load more
            </Button>
          </Flex>
        )}
      </Box>

      {/* Detail drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent bg="white" color="gray.800">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" borderColor="gray.200" pr={12} pt={6}>
            <HStack justify="space-between" align="center">
              <Box>
                <Text fontSize="xs" color="gray.400">
                  Admin log detail
                </Text>
                <Heading size="sm" mt={1}>
                  {selectedLog?.action || "Log entry"}
                </Heading>
              </Box>
              {selectedLog?.timestamp && (
                <Box mt={2}>
                  <Tag
                    size="sm"
                    variant="subtle"
                    colorScheme="teal"
                    borderRadius="full"
                  >
                    <TagLabel>
                      {formatTimestamp(selectedLog.timestamp)}
                    </TagLabel>
                  </Tag>
                </Box>
              )}
            </HStack>
          </DrawerHeader>

          <DrawerBody>
            {selectedLog ? (
              <VStack align="stretch" spacing={4} mt={2}>
                <Box>
                  <Text fontSize="xs" color="gray.400" mb={1}>
                    Summary
                  </Text>
                  <VStack
                    align="stretch"
                    spacing={2}
                    p={3}
                    borderRadius="lg"
                    bg="gray.50"
                  >
                    <HStack justify="space-between">
                      <Text fontSize="xs" color="gray.400">
                        Admin
                      </Text>
                      <Code fontSize="xs" bg="gray.100">
                        {selectedLog.adminUserId || "—"}
                      </Code>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="xs" color="gray.400">
                        Action
                      </Text>
                      <Tag
                        size="sm"
                        variant="outline"
                        colorScheme={
                          selectedLog.action?.includes("delete")
                            ? "red"
                            : selectedLog.action?.includes("update")
                            ? "yellow"
                            : "teal"
                        }
                      >
                        <TagLabel>{selectedLog.action || "—"}</TagLabel>
                      </Tag>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="xs" color="gray.400">
                        Target
                      </Text>
                      <Badge
                        variant="outline"
                        colorScheme={
                          selectedLog.targetId === "ALL"
                            ? "purple"
                            : selectedLog.targetId
                            ? "blue"
                            : "gray"
                        }
                        borderRadius="full"
                        px={2}
                      >
                        {selectedLog.targetId || "—"}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between" align="flex-start">
                      <Text fontSize="xs" color="gray.400">
                        Notes
                      </Text>
                      <Text fontSize="sm" maxW="70%">
                        {selectedLog.notes || "—"}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>

                <Box>
                  <Text fontSize="xs" color="gray.400" mb={1}>
                    Raw JSON
                  </Text>
                  <Box
                    borderRadius="lg"
                    bg="green.200"
                    p={3}
                    maxH="350px"
                    overflowY="auto"
                    borderWidth="1px"
                    borderColor="gray.200"
                  >
                    <Code
                      as="pre"
                      whiteSpace="pre"
                      fontSize="xs"
                      bg="transparent"
                    >
                      {JSON.stringify(selectedLog, null, 2)}
                    </Code>
                  </Box>
                </Box>
              </VStack>
            ) : (
              <Flex
                h="100%"
                align="center"
                justify="center"
                direction="column"
                color="gray.500"
              >
                <Text fontSize="sm">Select a log row to inspect details.</Text>
              </Flex>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default AdminLogsPage;
